import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

/**
 * Upload a single file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} path - Storage path (e.g., 'profiles/userId/profile-photo')
 * @param {number} maxSizeMB - Maximum file size in MB (default: 5MB)
 * @returns {Promise<string>} - Download URL of uploaded file
 */
export const uploadToStorage = async (file, path, maxSizeMB = 5) => {
  if (!file) {
    throw new Error('No file provided');
  }

  // Check file size
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    throw new Error(`File size exceeds ${maxSizeMB}MB limit`);
  }

  try {
    // Compress image if it's an image file
    let fileToUpload = file;
    if (file.type.startsWith('image/')) {
      fileToUpload = await compressImage(file);
    }

    // Create storage reference
    const storageRef = ref(storage, path);

    // Upload file
    const snapshot = await uploadBytes(storageRef, fileToUpload);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error('Failed to upload file. Please try again.');
  }
};

/**
 * Upload multiple files to Firebase Storage
 * @param {File[]} files - Array of files to upload
 * @param {string} basePath - Base storage path (e.g., 'profiles/userId/photos')
 * @returns {Promise<string[]>} - Array of download URLs
 */
export const uploadMultipleToStorage = async (files, basePath) => {
  if (!files || files.length === 0) {
    return [];
  }

  try {
    const uploadPromises = files.map((file, index) => {
      const path = `${basePath}/photo-${index}-${Date.now()}`;
      return uploadToStorage(file, path);
    });

    const downloadURLs = await Promise.all(uploadPromises);
    return downloadURLs;
  } catch (error) {
    console.error('Error uploading multiple files:', error);
    throw new Error('Failed to upload files. Please try again.');
  }
};

/**
 * Delete a file from Firebase Storage
 * @param {string} fileURL - Full download URL of the file
 */
export const deleteFromStorage = async (fileURL) => {
  if (!fileURL) return;

  try {
    // Extract path from URL
    const path = extractPathFromURL(fileURL);
    if (!path) {
      throw new Error('Invalid file URL');
    }

    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error('Failed to delete file.');
  }
};

/**
 * Compress an image file before uploading
 * @param {File} file - Image file to compress
 * @param {number} maxWidth - Maximum width in pixels (default: 1200)
 * @param {number} quality - Compression quality 0-1 (default: 0.8)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, maxWidth = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas to Blob conversion failed'));
              return;
            }

            // Create new file from blob
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            resolve(compressedFile);
          },
          file.type,
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Image load failed'));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('File read failed'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Extract storage path from download URL
 * @param {string} url - Firebase Storage download URL
 * @returns {string|null} - Extracted path or null
 */
const extractPathFromURL = (url) => {
  try {
    const baseURL = 'https://firebasestorage.googleapis.com/v0/b/';
    if (!url.startsWith(baseURL)) return null;

    const pathStart = url.indexOf('/o/') + 3;
    const pathEnd = url.indexOf('?');
    const encodedPath = url.substring(pathStart, pathEnd);
    return decodeURIComponent(encodedPath);
  } catch (error) {
    console.error('Error extracting path from URL:', error);
    return null;
  }
};

/**
 * Get file extension from filename
 * @param {string} filename - Name of the file
 * @returns {string} - File extension (e.g., 'jpg', 'png')
 */
export const getFileExtension = (filename) => {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
};

/**
 * Validate file type
 * @param {File} file - File to validate
 * @param {string[]} allowedTypes - Array of allowed MIME types
 * @returns {boolean} - True if valid
 */
export const validateFileType = (file, allowedTypes = ['image/jpeg', 'image/png', 'image/jpg']) => {
  return allowedTypes.includes(file.type);
};

/**
 * Generate unique filename
 * @param {string} originalName - Original filename
 * @param {string} userId - User ID
 * @returns {string} - Unique filename
 */
export const generateUniqueFilename = (originalName, userId) => {
  const timestamp = Date.now();
  const extension = getFileExtension(originalName);
  return `${userId}_${timestamp}.${extension}`;

};

/**
 * Upload image with compression
 * @param {File} file - The image file
 * @param {string} folder - Storage folder path
 * @param {object} options - Compression options
 * @returns {Promise<string>} - Download URL
 */
export const uploadImageWithCompression = async (
  file,
  folder = 'uploads',
  options = { maxWidth: 1200, maxHeight: 1200, quality: 0.85 }
) => {
  try {
    const compressedBlob = await compressImage(
      file,
      options.maxWidth,
      options.maxHeight,
      options.quality
    );

    const compressedFile = new File([compressedBlob], file.name, {
      type: file.type,
    });

    return await uploadToStorage(compressedFile, folder);
  } catch (error) {
    console.error('Compression failed, uploading original:', error);
    return await uploadToStorage(file, folder);
  }
};